import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedLogo } from "@/components/ui/animated-logo";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// Environment setup for API
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}api`;

const ComingSoon = () => {
    // Form and Submission State
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        service: "General Inquiry (Coming Soon Page)",
        message: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone || !formData.message) {
            toast.error("Please fill in all required fields.");
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.post(`${API}/inquiries`, formData);
            setIsSubmitted(true);
            toast.success("Thank you! We'll be in touch soon.");
        } catch (err) {
            console.error("Failed to submit inquiry:", err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15, delayChildren: 0.3 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" },
        },
    };

    return (
        <div className="relative min-h-screen bg-pc-dark flex items-center justify-center overflow-x-hidden font-body selection:bg-pc-accent/30 selection:text-white pt-6 pb-6 lg:py-0">
            {/* Subtle construction-themed background pattern */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none z-0">
                <svg
                    className="w-full h-full"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    viewBox="0 0 1200 800"
                >
                    <defs>
                        <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
                            <path d="M 80 0 L 0 0 0 80" fill="none" stroke="white" strokeWidth="0.8" />
                        </pattern>
                        <pattern id="diagonal" width="40" height="40" patternUnits="userSpaceOnUse">
                            <path d="M 0 40 L 40 0" fill="none" stroke="white" strokeWidth="0.4" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                    <rect width="100%" height="100%" fill="url(#diagonal)" />

                    {/* Structural beams */}
                    <line x1="20%" y1="0" x2="20%" y2="100%" stroke="white" strokeWidth="1.5" />
                    <line x1="80%" y1="0" x2="80%" y2="100%" stroke="white" strokeWidth="1.5" />
                    <line x1="0" y1="20%" x2="100%" y2="20%" stroke="white" strokeWidth="1" />
                    <line x1="0" y1="80%" x2="100%" y2="80%" stroke="white" strokeWidth="1" />

                    {/* Cross bracing */}
                    <line x1="20%" y1="20%" x2="80%" y2="80%" stroke="white" strokeWidth="0.6" />
                    <line x1="80%" y1="20%" x2="20%" y2="80%" stroke="white" strokeWidth="0.6" />
                </svg>
            </div>

            {/* Subtle radial glow */}
            <div
                className="absolute inset-0 pointer-events-none z-0"
                style={{
                    background: "radial-gradient(ellipse at center, rgba(201, 168, 76, 0.08) 0%, transparent 70%)",
                }}
            />

            {/* Split Layout Container */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-12 lg:py-0 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 xl:gap-24 min-h-screen lg:min-h-0">

                {/* Left Side: Brand & Details */}
                <motion.div
                    className="flex-1 flex flex-col justify-center text-center lg:text-left w-full max-w-lg lg:max-w-none mx-auto lg:mx-0 pt-6 lg:pt-0"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {/* Animated logo - Aligned left */}
                    <motion.div variants={itemVariants} className="mb-6 w-28 sm:w-32 lg:w-40 mx-auto lg:mx-0">
                        <AnimatedLogo width="100%" />
                    </motion.div>

                    {/* Main heading */}
                    <motion.h1 variants={itemVariants} className="font-heading font-extrabold text-white text-5xl sm:text-6xl lg:text-[68px] tracking-tight leading-[1.05] mb-4">
                        Prayosha<br />
                        <span className="text-gray-400 font-light">Construction</span>
                    </motion.h1>

                    {/* Tagline */}
                    <motion.p variants={itemVariants} className="text-lg sm:text-xl text-gray-400 leading-snug max-w-md mb-6 mx-auto lg:mx-0">
                        A legacy of trust. We're building a new digital experience to serve you better.
                    </motion.p>

                    {/* Launch text */}
                    <motion.div variants={itemVariants} className="mb-8">
                        <p className="text-sm font-heading font-bold tracking-[0.25em] uppercase text-pc-accent mb-1">
                            Launching
                        </p>
                        <p className="text-xl sm:text-2xl text-white font-medium">March 2026</p>
                    </motion.div>

                    {/* Contact Quick Info */}
                    <motion.div variants={itemVariants} className="flex flex-col gap-3 mx-auto lg:mx-0">
                        <div className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group">
                            <div className="w-8 h-8 rounded-full border border-gray-700/50 flex items-center justify-center group-hover:border-pc-accent/50 transition-colors shrink-0">
                                <Phone className="w-3.5 h-3.5 text-pc-accent" />
                            </div>
                            <a href="tel:+919979042010" className="text-sm font-medium tracking-wide">+91 99790 42010</a>
                        </div>

                        <div className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group">
                            <div className="w-8 h-8 rounded-full border border-gray-700/50 flex items-center justify-center group-hover:border-pc-accent/50 transition-colors shrink-0">
                                <Mail className="w-3.5 h-3.5 text-pc-accent" />
                            </div>
                            <a href="mailto:construction.prayosha@gmail.com" className="text-sm font-medium tracking-wide">construction.prayosha@gmail.com</a>
                        </div>

                        <div className="flex items-center gap-3 text-gray-400 group">
                            <div className="w-8 h-8 rounded-full border border-gray-700/50 flex items-center justify-center group-hover:border-pc-accent/50 transition-colors shrink-0">
                                <MapPin className="w-3.5 h-3.5 text-pc-accent" />
                            </div>
                            <a href="https://maps.app.goo.gl/efd7P2xJ4NUCpgdX9" className="text-sm font-medium tracking-wide">Anand, Gujarat, India</a>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Right Side: Contact Form Area */}
                <motion.div
                    className="w-full lg:w-[440px] xl:w-[480px] shrink-0 pb-10 lg:pb-0"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <motion.div
                        variants={itemVariants}
                        className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 lg:p-8 shadow-2xl relative overflow-hidden"
                    >
                        {/* Subtle inner top highlight */}
                        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.15] to-transparent" />

                        <AnimatePresence mode="wait">
                            {isSubmitted ? (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.4 }}
                                    className="flex flex-col items-center justify-center text-center py-10"
                                >
                                    <div className="w-14 h-14 rounded-full bg-pc-accent/20 flex items-center justify-center mb-5">
                                        <CheckCircle2 className="w-7 h-7 text-pc-accent" />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold text-white mb-2">
                                        Received!
                                    </h3>
                                    <p className="text-gray-400 text-sm leading-relaxed mb-6">
                                        Thank you for reaching out. We will get back to you within 24 hours to discuss your project.
                                    </p>
                                    <button
                                        onClick={() => setIsSubmitted(false)}
                                        className="text-pc-accent hover:text-white text-sm font-semibold tracking-wider uppercase transition-colors"
                                    >
                                        Send Another Message
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="form"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <h3 className="font-heading text-xl font-bold text-white mb-1">Get in touch</h3>
                                    <p className="text-gray-400 text-sm mb-6">Have a project in mind? We'd love to hear about it.</p>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="Full Name *"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full h-11 bg-black/20 border border-white/10 rounded-lg px-4 text-white placeholder:text-gray-500 font-body text-sm focus:outline-none focus:border-pc-accent/60 focus:ring-1 focus:ring-pc-accent/60 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                name="phone"
                                                placeholder="Phone Number *"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                                className="w-full h-11 bg-black/20 border border-white/10 rounded-lg px-4 text-white placeholder:text-gray-500 font-body text-sm focus:outline-none focus:border-pc-accent/60 focus:ring-1 focus:ring-pc-accent/60 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email Address"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full h-11 bg-black/20 border border-white/10 rounded-lg px-4 text-white placeholder:text-gray-500 font-body text-sm focus:outline-none focus:border-pc-accent/60 focus:ring-1 focus:ring-pc-accent/60 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <textarea
                                                name="message"
                                                placeholder="Brief project details... *"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                className="w-full min-h-[100px] bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-500 font-body text-sm focus:outline-none focus:border-pc-accent/60 focus:ring-1 focus:ring-pc-accent/60 transition-all resize-y"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full h-11 bg-pc-accent hover:bg-[#b0923d] text-white font-heading font-semibold tracking-wide rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed group mt-2"
                                        >
                                            {isSubmitting ? "Sending..." : "Send Message"}
                                            {!isSubmitting && <Send className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />}
                                        </button>
                                    </form>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default ComingSoon;
